// mirror of auware/.../frontend/src/components/ui/Select.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type SelectHTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Label displayed above the select. */
  label?: string;
  /** Error message displayed below the select. */
  error?: string;
  /** Helper text displayed below the select (hidden when `error` is set). */
  helperText?: string;
  /** Options to render inside the select. */
  options: SelectOption[];
  /** Placeholder text shown as a disabled first option. */
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseSelectClasses =
  "mt-1 block w-full rounded-lg border-neutral-300 py-2 pl-3 pr-8 shadow-sm " +
  "focus:border-primary-500 focus:ring-primary-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

const errorSelectClasses =
  "mt-1 block w-full rounded-lg border-red-500 py-2 pl-3 pr-8 shadow-sm " +
  "focus:border-red-500 focus:ring-red-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Dropdown select with optional label, error state, and helper text.
 *
 * ```tsx
 * <Select
 *   label="Country"
 *   placeholder="Choose a country"
 *   options={[{ value: "us", label: "United States" }]}
 *   error={errors.country}
 * />
 * ```
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className = "",
      label,
      error,
      helperText,
      id,
      options,
      placeholder,
      ...props
    },
    ref,
  ) => {
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-neutral-700"
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={id}
          className={`${error ? errorSelectClasses : baseSelectClasses} ${className}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? `${id}-error` : helperText ? `${id}-helper` : undefined
          }
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${id}-helper`} className="mt-1 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
